<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import SurfacePostsGrid from '@@/vuecomponents/SurfacePostsGrid.vue'
import {
  SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE,
  SURFACE_POSTS_GRID_SECTION_HEADER,
} from '@@/vuecomponents/SurfacePostsGridSection.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

if (!device.mobile) useDoubleClickToPost()

const { xHeader, backgroundLuminance, isReadOnly, isScreenshotMode } = storeToRefs(useSurfaceStore())
const SurfacePostsGridVirtualized = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostsGridVirtualized.vue'),
)
const containerRef = ref<HTMLDivElement | null>(null)
useAutoScrollElement({
  scrollContainerElementRef: containerRef,
  type: [SURFACE_POSTS_GRID_SECTION_HEADER, SURFACE_POSTS_GRID_POST_DRAGGABLE_TYPE],
})
</script>

<template>
  <div
    id="wall-container"
    ref="containerRef"
    :class="{
      flex: true,
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
      'w-full h-full': isScreenshotMode,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <SurfacePostsGridVirtualized
      v-if="isAppUsing('virtualizedPostsGrid')"
      class="grow"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
    <SurfacePostsGrid
      v-else
      class="grow"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
    />
  </div>
</template>
